/**
 * 新增记录弹窗组件
 * 用于在项目概览弹窗中直接添加工时/设备/材料记录
 */
import React, { useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Input, InputNumber, Button, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { WorkTimeService } from '../../services/workTimeService';
import { EquipmentUsageService } from '../../services/equipmentUsageService';
import { MaterialUsageService } from '../../services/materialUsageService';
import { PresetDataService } from '../../services/presetDataService';
import { equipmentPriceService } from '../../services/equipmentPriceService';
import { materialPriceService } from '../../services/materialPriceService';
import CascadingSelect, { CascadingSelectOption } from '../../components/CascadingSelect';
import BatchWorkTimeInput, { WorkTimeEntry } from '../../components/BatchWorkTimeInput';
import { equipmentCategoryLabels, materialCategoryLabels } from '../../utils/category';
import type { CreateWorkTimeData, CreateEquipmentUsageData, CreateMaterialUsageData, WorkerType } from '../../types';

const { TextArea } = Input;
const { Option } = Select;

export type RecordType = 'worktime' | 'equipment' | 'material';

interface AddRecordModalProps {
  visible: boolean;
  recordType: RecordType | null;
  projectId: string;
  projectName: string;
  onClose: () => void;
  onSuccess: () => void;
  isMobile?: boolean;
}

// 记录类型配置
const RECORD_TYPE_CONFIG = {
  worktime: { title: '添加工时记录', icon: '👷' },
  equipment: { title: '添加设备记录', icon: '🔧' },
  material: { title: '添加材料记录', icon: '📦' }
};

const AddRecordModal: React.FC<AddRecordModalProps> = ({
  visible,
  recordType,
  projectId,
  projectName,
  onClose,
  onSuccess,
  isMobile = false
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // 重置表单
  useEffect(() => {
    if (visible && recordType) {
      form.resetFields();
      // 设置默认值
      form.setFieldsValue({
        usageDate: dayjs(),
        workDate: dayjs(),
        workTimeEntries: [{
          id: '1',
          workerType: 'male',
          workUnits: 1,
          workerCount: 1
        }]
      });
    }
  }, [visible, recordType, form]);

  // ========== 工时相关 ==========
  const createWorkTimeMutation = useMutation({
    mutationFn: (data: CreateWorkTimeData) => WorkTimeService.createWorkTime(data),
    onSuccess: () => {
      message.success('工时记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['workTimes'] });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    }
  });

  // ========== 设备相关 ==========
  const { data: presetEquipmentData } = useQuery({
    queryKey: ['preset-equipment', 'all'],
    queryFn: () => PresetDataService.getEquipment({ limit: 1000 }),
    enabled: visible && recordType === 'equipment'
  });

  const [selectedEquipmentName, setSelectedEquipmentName] = React.useState<string>('');

  const { data: equipmentPriceData } = useQuery({
    queryKey: ['equipment-price', selectedEquipmentName],
    queryFn: async () => {
      if (!selectedEquipmentName) return null;
      try {
        const response = await equipmentPriceService.getEquipmentPrices({ 
          equipmentName: selectedEquipmentName 
        });
        const prices = response?.data || [];
        const matchedPrice = prices.find((p: any) => 
          p.equipmentName === selectedEquipmentName || 
          p.equipmentName?.includes(selectedEquipmentName)
        );
        return matchedPrice ? { price: matchedPrice.price, unit: matchedPrice.unit || '台班' } : null;
      } catch (error) {
        return null;
      }
    },
    enabled: !!selectedEquipmentName && recordType === 'equipment'
  });

  const createEquipmentMutation = useMutation({
    mutationFn: (data: CreateEquipmentUsageData) => EquipmentUsageService.createEquipmentUsage(data),
    onSuccess: () => {
      message.success('设备记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['equipmentUsages'] });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    }
  });

  const equipmentOptions: CascadingSelectOption[] = React.useMemo(() => {
    const presetEquipments = (presetEquipmentData as any)?.data || [];
    const uniqueMap = new Map<string, CascadingSelectOption>();
    presetEquipments.forEach((equipment: any) => {
      if (equipment.equipmentName && !uniqueMap.has(equipment.equipmentName)) {
        uniqueMap.set(equipment.equipmentName, {
          name: equipment.equipmentName,
          category: equipment.category || '其他',
          unit: equipment.unit,
          specifications: equipment.specifications,
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [presetEquipmentData]);

  // ========== 材料相关 ==========
  const { data: presetMaterialsData } = useQuery({
    queryKey: ['preset-materials', 'all'],
    queryFn: () => PresetDataService.getMaterials({ limit: 500 }),
    enabled: visible && recordType === 'material'
  });

  const { data: commonUnitsData } = useQuery({
    queryKey: ['common-units'],
    queryFn: () => MaterialUsageService.getCommonUnits(),
    enabled: visible && recordType === 'material'
  });

  const [selectedMaterialName, setSelectedMaterialName] = React.useState<string>('');

  const { data: materialPriceList } = useQuery({
    queryKey: ['material-price-list', selectedMaterialName],
    queryFn: async () => {
      if (!selectedMaterialName) return [];
      try {
        const response = await materialPriceService.getMaterialPrices({ 
          materialName: selectedMaterialName 
        });
        return response?.data || [];
      } catch (error) {
        return [];
      }
    },
    enabled: !!selectedMaterialName && recordType === 'material'
  });

  const createMaterialMutation = useMutation({
    mutationFn: (data: CreateMaterialUsageData) => MaterialUsageService.createMaterialUsage(data),
    onSuccess: () => {
      message.success('材料记录创建成功');
      queryClient.invalidateQueries({ queryKey: ['materialUsages'] });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    }
  });

  const materialOptions: CascadingSelectOption[] = React.useMemo(() => {
    const presetMaterials = (presetMaterialsData as any)?.data || [];
    const uniqueMap = new Map<string, CascadingSelectOption>();
    presetMaterials.forEach((material: any) => {
      if (material.materialName && !uniqueMap.has(material.materialName)) {
        uniqueMap.set(material.materialName, {
          name: material.materialName,
          category: material.category || '其他',
          unit: material.defaultUnit,
          specifications: material.specifications,
        });
      }
    });
    return Array.from(uniqueMap.values());
  }, [presetMaterialsData]);

  // 获取材料的默认单位
  const getMaterialDefaultUnit = (materialName: string): string => {
    const presetMaterials = (presetMaterialsData as any)?.data || [];
    const material = presetMaterials.find((m: any) => m.materialName === materialName);
    return material?.defaultUnit || '';
  };

  // ========== 表单提交 ==========
  const handleSubmit = async (values: any) => {
    if (recordType === 'worktime') {
      const workTimeEntries: WorkTimeEntry[] = values.workTimeEntries || [];
      if (workTimeEntries.length === 0) {
        message.error('请至少添加一个工时条目');
        return;
      }

      for (const entry of workTimeEntries) {
        const formData: CreateWorkTimeData = {
          projectId,
          workDate: values.workDate.format('YYYY-MM-DD'),
          workerType: entry.workerType as WorkerType,
          workUnits: entry.workUnits,
          workContent: values.workContent || '',
          maleWorkerCount: entry.workerType === 'male' ? entry.workerCount : 0,
          femaleWorkerCount: entry.workerType === 'female' ? entry.workerCount : 0,
        };
        await createWorkTimeMutation.mutateAsync(formData);
      }
    } else if (recordType === 'equipment') {
      const formData: CreateEquipmentUsageData = {
        projectId,
        equipmentName: values.equipmentName,
        usageDate: values.usageDate.format('YYYY-MM-DD'),
        usageHours: values.usageHours,
        cost: values.cost,
      };
      createEquipmentMutation.mutate(formData);
    } else if (recordType === 'material') {
      const formData: CreateMaterialUsageData = {
        projectId,
        materialName: values.materialName,
        usageDate: values.usageDate.format('YYYY-MM-DD'),
        quantity: values.quantity,
        unit: values.unit,
        unitPrice: values.unitPrice,
      };
      createMaterialMutation.mutate(formData);
    }
  };

  // ========== 设备选择处理 ==========
  const handleEquipmentSelect = (value: string) => {
    form.setFieldValue('equipmentName', value);
    setSelectedEquipmentName(value);
  };

  const handleUsageHoursChange = (value: number | null) => {
    if (equipmentPriceData && value) {
      const dailyRate = equipmentPriceData.price;
      const unit = equipmentPriceData.unit;
      let hourlyRate = dailyRate;
      if (['台班', '天', 'day', 'shift'].includes(unit)) {
        hourlyRate = dailyRate / 8;
      }
      const calculatedCost = Math.round(hourlyRate * value * 100) / 100;
      form.setFieldValue('cost', calculatedCost);
    }
  };

  // ========== 材料选择处理 ==========
  const handleMaterialSelect = (value: string) => {
    form.setFieldValue('materialName', value);
    setSelectedMaterialName(value);
    const defaultUnit = getMaterialDefaultUnit(value);
    if (defaultUnit) {
      form.setFieldValue('unit', defaultUnit);
    }
  };

  const handleUnitChange = (value: string) => {
    if (materialPriceList && value) {
      const match = (materialPriceList as any[]).find((p: any) => p.unit === value);
      if (match) {
        form.setFieldValue('unitPrice', match.price);
        message.info(`已自动匹配单位 "${value}" 的价格: ¥${match.price}`);
      }
    }
  };

  // 加载状态
  const isLoading = createWorkTimeMutation.isPending || 
                    createEquipmentMutation.isPending || 
                    createMaterialMutation.isPending;

  if (!recordType) return null;

  const config = RECORD_TYPE_CONFIG[recordType];

  // 渲染工时表单
  const renderWorkTimeForm = () => (
    <>
      <Form.Item
        name="workDate"
        label="工作日期"
        rules={[{ required: true, message: '请选择工作日期' }]}
      >
        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item
        name="workTimeEntries"
        label="工时条目"
        rules={[{
          validator: (_: any, value: WorkTimeEntry[]) => {
            if (!value || value.length === 0) {
              return Promise.reject(new Error('请至少添加一个工时条目'));
            }
            return Promise.resolve();
          }
        }]}
      >
        <BatchWorkTimeInput disabled={isLoading} maxRows={10} />
      </Form.Item>

      <Form.Item
        name="workContent"
        label="施工内容"
        rules={[
          { required: true, message: '请输入施工内容' },
          { max: 500, message: '施工内容不能超过500个字符' }
        ]}
      >
        <TextArea rows={3} placeholder="请输入施工内容" showCount maxLength={500} />
      </Form.Item>
    </>
  );

  // 渲染设备表单
  const renderEquipmentForm = () => (
    <>
      <Form.Item
        name="equipmentName"
        label="设备名称"
        rules={[{ required: true, message: '请选择设备' }]}
      >
        <CascadingSelect
          options={equipmentOptions}
          placeholder="请选择设备分类和名称"
          onChange={handleEquipmentSelect}
          categoryLabels={equipmentCategoryLabels}
        />
      </Form.Item>

      <Form.Item
        name="usageDate"
        label="使用日期"
        rules={[{ required: true, message: '请选择使用日期' }]}
      >
        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item
        name="usageHours"
        label="使用时长（小时）"
        rules={[{ required: true, message: '请输入使用时长' }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          placeholder="请输入使用时长"
          min={0}
          max={24}
          step={0.5}
          precision={1}
          onChange={handleUsageHoursChange}
        />
      </Form.Item>

      <Form.Item
        name="cost"
        label={
          <span>
            设备成本（元）
            {equipmentPriceData && (
              <span style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                (¥{equipmentPriceData.price}/{equipmentPriceData.unit})
              </span>
            )}
          </span>
        }
      >
        <InputNumber
          style={{ width: '100%' }}
          placeholder="系统将自动计算"
          min={0}
          step={0.01}
          precision={2}
        />
      </Form.Item>
    </>
  );

  // 渲染材料表单
  const renderMaterialForm = () => (
    <>
      <Form.Item
        name="materialName"
        label="材料名称"
        rules={[{ required: true, message: '请选择材料' }]}
      >
        <CascadingSelect
          options={materialOptions}
          placeholder="请选择材料分类和名称"
          onChange={handleMaterialSelect}
          categoryLabels={materialCategoryLabels}
        />
      </Form.Item>

      <Form.Item
        name="usageDate"
        label="使用日期"
        rules={[{ required: true, message: '请选择使用日期' }]}
      >
        <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item
        name="quantity"
        label="使用数量"
        rules={[{ required: true, message: '请输入使用数量' }]}
      >
        <InputNumber
          style={{ width: '100%' }}
          placeholder="请输入使用数量"
          min={0}
          step={0.01}
          precision={2}
        />
      </Form.Item>

      <Form.Item
        name="unit"
        label="计量单位"
        rules={[{ required: true, message: '请选择计量单位' }]}
      >
        <Select
          placeholder="请选择计量单位"
          showSearch
          allowClear
          onChange={handleUnitChange}
        >
          {(commonUnitsData as any)?.data?.map((unit: string) => (
            <Option key={unit} value={unit}>{unit}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item name="unitPrice" label="单价（元）">
        <InputNumber
          style={{ width: '100%' }}
          placeholder="请输入单价（可选）"
          min={0}
          step={0.01}
          precision={2}
        />
      </Form.Item>
    </>
  );

  return (
    <Modal
      title={`${config.icon} ${config.title} - ${projectName}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100%' : 520}
      style={isMobile ? { top: 20, margin: '0 auto', maxWidth: '100%' } : {}}
      destroyOnClose
      zIndex={1001}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        disabled={isLoading}
        style={{ marginTop: 16 }}
      >
        {recordType === 'worktime' && renderWorkTimeForm()}
        {recordType === 'equipment' && renderEquipmentForm()}
        {recordType === 'material' && renderMaterialForm()}

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              创建
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddRecordModal;
